import { Server } from '../../server/server';
import {GameBase} from '../common/gameBase';
import {ParticleManager} from '../common/particleManager';
import {RandomNumberGenerator} from '../common/randomNumberGenerator';
// import {RoomDialog} from 'src/common/roomDialog';
// import {InviteFriendsDialog} from 'src/common/inviteFriendsDialog';
// import {Site} from 'src/site/site';
import {SpriteSheetManager} from '../common/spriteSheetManager';
import {getDeviceType, hashString} from '../common/util';
import {GameOverDialog} from './gameOverDialog';
import {GameScreen} from './gameScreen';
import {GameStyle} from './gameStyle';
import {ArcadeBoard, MatchThree, MatchThreeState} from './matchThree';
import {SplashScreen} from './splashScreen';
import Levels from './assets/levels.json';
import { GameOptionsDialog } from './gameOptionsDialog';
import { PupSlotsDialog } from './pupSlotsDialog';
import { PupListDialog } from './pupListDialog';
import { MapScreen } from './mapScreen';

export enum GameMode {
  Arcade,
  Adventure
}

export class Game extends GameBase {
  public static instance: Game = null;

  public spriteSheetManager: SpriteSheetManager;
  public particleManager: ParticleManager;
  public matchThree: MatchThree;

  public splashScreen: SplashScreen;
  public mapScreen: MapScreen;
  public gameScreen: GameScreen;
  public gameOverDialog: GameOverDialog;
  public gameOptionsDialog: GameOptionsDialog;
  public pupSlotsDialog: PupSlotsDialog;
  public pupListDialog: PupListDialog;

  public playing: boolean;
  public gameLaunched: boolean;
  public version: string;
  public deviceInfo: string;
  public randGen: RandomNumberGenerator;

  public gameMode: GameMode;
  public levelSelected: number;

  constructor(canvas:HTMLCanvasElement, callback:Function, gameMode: number, tournament:boolean) {
    super(canvas, callback, gameMode === GameMode.Arcade ? 'matcha-blitz' : 'matcha-adventure', tournament);
    this.version = '0.1.0.3';
    this.deviceInfo = `**${getDeviceType()}** - ${navigator.platform} - ${navigator.appVersion}`;
    this.playing = false;
    this.gameLaunched = false;
    this.dialogStyle = GameStyle.dialog;
    this.gameMode = gameMode;
  }

  public load() {
    super.load();

    Game.instance = this;

    let seed = this.getInitialSeed();
    this.randGen = new RandomNumberGenerator(seed);

    this.onKeyDown = this.onKeyDown.bind(this);
    document.addEventListener('keydown', this.onKeyDown);

    this.spriteSheetManager = new SpriteSheetManager();
    this.particleManager = new ParticleManager();
    this.matchThree = new MatchThree();

    // this.roomDialog = new RoomDialog(GameStyle.dialog);
    // this.inviteFriendsDialog = new InviteFriendsDialog(GameStyle.dialog, GameStyle.inviteFriendsDialog);

    this.pupListDialog = new PupListDialog(GameStyle.dialog);
    this.pupListDialog.build();

    this.pupSlotsDialog = new PupSlotsDialog(GameStyle.dialog);
    this.pupSlotsDialog.build();

    this.gameOptionsDialog = new GameOptionsDialog(GameStyle.dialog);
    this.gameOptionsDialog.build();

    this.splashScreen = new SplashScreen();
    this.splashScreen.build();

    this.mapScreen = new MapScreen();
    this.mapScreen.build();

    this.gameScreen = new GameScreen();
    this.gameScreen.build();

    this.gameOverDialog = new GameOverDialog();
    this.gameOverDialog.build();

    this.splashScreen.show();
  };

  public unload() {
    // this.roomDialog = null;
    // this.inviteFriendsDialog = null;

    this.splashScreen = null;
    this.mapScreen = null;
    this.gameScreen = null;
    this.gameOverDialog = null;
    this.gameOptionsDialog = null;
    this.pupListDialog = null;
    this.pupSlotsDialog = null;

    this.matchThree = null;
    this.spriteSheetManager = null;
    this.particleManager = null;

    this.randGen = null;
    document.removeEventListener('keydown', this.onKeyDown);
    Game.instance = null;
    super.unload();
  };

  public tick(deltaTime: number) {
    this.randGen.getRand();
    this.matchThree.update(deltaTime);
  }

  public finish() {
    // super.finish();

    this.playing = false;
    this.matchThree.setState(MatchThreeState.Limbo);

    if (this.matchThree.isGameWon())
      this.matchThree.handleWin();
    else
      this.matchThree.handleLoss();

    // let lifetimeScore = Site.cookies.getNumber('MatchThreeLifetimeScore');
    // lifetimeScore += this.matchThree.totalScore;
    // Site.cookies.setNumber('MatchThreeLifetimeScore', lifetimeScore);
  }

  protected stringifySettings(settings: any): string {
    let s = `${settings.boardNumber}~${settings.timedGame}~${settings.minutesAllowed}~${settings.movesAllowed}~${
        settings.playingMatch3Saga}~${settings.seed}`;
    return s;
  }

  protected parseSettings(s: string): any {
    let parts = s.split('~');
    let settings = {
      boardNumber : parseInt(parts[0]),
      timedGame : (parts[1] == 'true') ? true : false,
      minutesAllowed : parseInt(parts[2]),
      movesAllowed : parseInt(parts[3]),
      playingMatch3Saga : (parts[4] == 'true') ? true : false,
      seed : parseInt(parts[5])
    };
    return settings;
  }

  /**
   * Gets a random seed using milliseconds since Jan 1, 1970.
   */
  public getInitialSeed(): number {
    let d = new Date();
    let t = d.getTime();  // milliseconds since Jan 1, 1970
    let h = hashString(`${t}`);
    return (t + h);
  }

  public showResults() {
    this.gameOverDialog.showModal();
  }

  /**
   * Called when a key is pressed. Will then route to the correct
   * place that will process it.
   *
   * @param event - Contains info on key press.
   */
  public onKeyDown(event: KeyboardEvent) {
    // For testing and debugging
    this.matchThree.onKeyDown(event);
  }

  //-----------------------------
  // The Start Game Workflow
  public start() {
    if (this.gameMode == GameMode.Arcade)
      this.arcadeMode();
    else
      this.adventureMode();
  }

  public arcadeMode() {
    this.startGame(ArcadeBoard); 
  }

  public adventureMode() {
    if (this.levelSelected)
      this.startGame(Levels[this.levelSelected - 1]);
    else
      this.mapScreen.show();
  }

  public startGame(boardInfo: any) {
    if(this.matchThree.autoPlay)
      this.startGame2(boardInfo);
    else {
      this.registerGame();
      this.pupSlotsDialog.showModal(async () => {
        this.startGame2(boardInfo);
      });
    }
  }

  protected startGame2(boardInfo: any) {
    // super.start(settings);
    this.matchThree.startGame(boardInfo);
    this.gameScreen.startGame();
    this.gameLaunched = true;
    this.playing = true;
  }

  public getAdventureCookie() {
    let cookieStr = Server.user.getCookie('matcha-adventure');
    return cookieStr ? JSON.parse(cookieStr) : {level: 1, score: 0};
  }

  public setAdventureCookie(level:number, score:number) {
    let cookie = {level, score};
    Server.user.setCookie('matcha-adventure', JSON.stringify(cookie));
  }
}
